import { useRef } from "react";
import AnchoredPanel from "../../../../GlobalComponents/AnchoredPanel/AnchoredPanel";

let radioGroupCounter = 0;

/**
 * Editor for a KPI's unit conversion table, anchored under the cell.
 *
 * `supported_units` is not a list of unit names — it is
 * `[{ unit_id, factor, is_base }]`, where every factor converts that unit into
 * the base. So the editor is a small table: one radio column choosing the
 * base, one number column for the multiplier. The base's own factor is fixed
 * at 1 and shown disabled, because it converts into itself.
 *
 * @param {object} props
 * @param {{ unitId: number, factor: number, isBase: boolean }[]} props.units
 * @param {{ id: number, unit: string, unit_name: string }[]} props.unitMaster
 * @param {string} [props.error]
 * @param {(unitId: number) => void} props.onAdd
 * @param {(unitId: number) => void} props.onRemove
 * @param {(unitId: number) => void} props.onSetBase
 * @param {(unitId: number, factor: number) => void} props.onFactor
 * @param {() => void} props.onClose
 */
export default function UnitRowsEditor({
  anchorRef,
  units,
  unitMaster,
  error,
  onAdd,
  onRemove,
  onSetBase,
  onFactor,
  onClose,
}) {
  const groupRef = useRef(null);
  if (groupRef.current === null) groupRef.current = ++radioGroupCounter;


  const labelFor = (unitId) => {
    const found = unitMaster.find((unit) => unit.id === unitId);
    return found ? found.unit : `unit ${unitId}`;
  };

  const unused = unitMaster.filter((unit) => !units.some((u) => u.unitId === unit.id));

  return (
    <AnchoredPanel anchorRef={anchorRef} onClose={onClose} className="kgb-picker kgb-picker--wide">
      <p className="kgb-picker-title">Base unit and conversion factors</p>

      {units.length === 0 ? <p className="kgb-picker-empty">No units yet</p> : null}

      {units.map((unit) => (
        <div className="kgb-editor-row" key={unit.unitId}>
          <label className="kgb-editor-base">
            <input
              type="radio"
              name={`kgb-base-${groupRef.current}`}
              checked={unit.isBase}
              aria-label={`Make ${labelFor(unit.unitId)} the base unit`}
              onChange={() => onSetBase(unit.unitId)}
            />
            <span className="kgb-editor-unit">{labelFor(unit.unitId)}</span>
          </label>
          <input
            type="number"
            className="kgb-editor-factor"
            value={unit.factor}
            min="0"
            step="any"
            disabled={unit.isBase}
            aria-label={`Factor converting ${labelFor(unit.unitId)} into the base unit`}
            onChange={(e) => onFactor(unit.unitId, e.target.value)}
          />
          <button
            type="button"
            className="kgb-editor-remove"
            aria-label={`Remove ${labelFor(unit.unitId)}`}
            onClick={() => onRemove(unit.unitId)}
          >
            ×
          </button>
        </div>
      ))}

      {error ? <p className="kgb-editor-error">{error}</p> : null}

      <div className="kgb-editor-actions">
        <select
          className="kgb-editor-key"
          value=""
          aria-label="Add a unit"
          onChange={(e) => e.target.value && onAdd(Number(e.target.value))}
        >
          <option value="">Add a unit…</option>
          {unused.map((unit) => (
            <option key={unit.id} value={unit.id}>
              {unit.unit} — {unit.unit_name}
            </option>
          ))}
        </select>
      </div>
    </AnchoredPanel>
  );
}
